import { addMilliseconds, isAfter } from 'date-fns';
import jsCookie from 'js-cookie';

import { IAuth } from '@/modules/auth/types';
// Define an interface for the stored item
interface SessionStorageItem<T> {
  value: T;
  expiry: string; // Store expiry as an ISO string
}

export const useSessionStorage = () => {
  const hasStorage =
    typeof window !== 'undefined' &&
    'sessionStorage' in window &&
    window.sessionStorage;

  // Function to set item in session storage with expiry time
  const setSessionStorageWithExpiry = <T>(
    key: string,
    value: T,
    ttl: number = 24 * 60 * 60 * 1000
  ): void => {
    // Calculate the expiry date by adding ttl (milliseconds) to the current date
    const expiryDate = addMilliseconds(new Date(), ttl).toISOString();

    // Create an object to store the original value and the expiration time
    const item: SessionStorageItem<T> = {
      value,
      expiry: expiryDate,
    };

    // Set the item in session storage as a JSON string
    sessionStorage.setItem(key, JSON.stringify(item));
  };

  // Function to retrieve an item from session storage
  const getSessionStorageWithExpiry = <T>(key: string): T | null => {
    // Check if there's storage
    if (!hasStorage) {
      return null;
    }

    // Retrieve the item from session storage
    const itemStr = sessionStorage.getItem(key);

    // If the item doesn't exist, return null
    if (!itemStr) {
      return null;
    }

    // Parse the JSON string back to an object
    const item: SessionStorageItem<T> = JSON.parse(itemStr);

    // Check if the current date is after the expiry date
    const now = new Date();
    if (isAfter(now, new Date(item.expiry))) {
      // If the item is expired, remove it from storage and return null
      sessionStorage.removeItem(key);
      return null;
    }

    // Return the value if it is not expired
    return item.value;
  };

  const clearSessionStorage = (key?: string) => {
    if (hasStorage) {
      if (key) sessionStorage.removeItem(key);
      else sessionStorage.clear();
    }
  };

  return {
    getSessionStorageWithExpiry,
    setSessionStorageWithExpiry,
    clearSessionStorage,
  };
};

export const useLocalStorage = () => {
  const hasStorage =
    typeof window !== 'undefined' &&
    'localStorage' in window &&
    window.localStorage;

  const setLocalStorage = <T>(key: string, value: T): void => {
    if (hasStorage) localStorage.setItem(key, JSON.stringify(value));
  };

  const getlocalStorage = <T>(key: string): T | null => {
    if (!hasStorage) {
      return null;
    }
    const itemStr = localStorage.getItem(key);

    if (!itemStr) {
      return null;
    }

    const item = JSON.parse(itemStr);

    return item.value;
  };

  const clearLocalStorage = (key?: string) => {
    if (hasStorage) {
      if (key) localStorage.removeItem(key);
      else localStorage.clear();
    }
  };

  return { getlocalStorage, setLocalStorage, clearLocalStorage };
};

export const useCookies = () => {
  const getCookie = (key: string) => {
    return jsCookie.get(key);
  };
  const getToken = () => {
    return getCookie('token');
  };
  const getUserObj = () => {
    const userObj = getCookie('userObj');
    const user: IAuth.IUser | undefined = JSON.parse(userObj ?? '{}')?.value;
    return user;
  };

  return { getCookie, getToken, getUserObj };
};

export const clearStoreData = () => {
  if (typeof window !== 'undefined') {
    localStorage.removeItem('storeData');
    sessionStorage.clear();
  }
};
